import { useRef, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentsQuery, uploadDocument } from '../api/jedi'
import { apiDownload } from '../auth/apiFetch'
import { useEngagement } from '../lib/engagement'
import { usePersonZone, whenIn } from '../lib/personTime'
import { InstanceProfile } from '../shell/InstanceProfile'
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL, oversizeWords } from '../shell/uploadLimits'
import type { DocumentRow } from '../api/types'

/**
 * Which half of Inputs is open: the documents, or the customer's ServiceNow instance as J.E.D.I. has read
 * it (RFC-0017's 2026-10-10 amendment), and which connection. Validated rather than trusted.
 */
type InputsSearch = { view?: 'servicenow'; connection?: string }

export const Route = createFileRoute('/_shell/inputs')({
  component: Inputs,
  validateSearch: (search: Record<string, unknown>): InputsSearch => ({
    ...(search.view === 'servicenow' ? { view: 'servicenow' as const } : {}),
    ...(typeof search.connection === 'string' && search.connection ? { connection: search.connection } : {}),
  }),
})


/**
 * Inputs: the one document ingress (ADR-0029) and, since wave-7 step 4, the
 * library beneath it — every document with the state its ingest actually
 * reached. Findings review and gap detection ("a meeting happened and no
 * transcript arrived") grow in on this surface.
 */
function Inputs() {
  const queryClient = useQueryClient()
  const { id: engagementId } = useEngagement()
  const { view, connection } = Route.useSearch()
  const navigate = Route.useNavigate()
  const instance = view === 'servicenow'
  const fileInput = useRef<HTMLInputElement>(null)
  const [lastUpload, setLastUpload] = useState<string | null>(null)
  const [oversize, setOversize] = useState<string | null>(null)
  /*
    ROUTE ITS STATEMENTS, OR ONLY READ IT (Erik's demo-environment rulings,
    2026-10-22). On by default, as every upload has been: what the document
    says becomes proposals on Home. Off, it is read and kept (searchable,
    citable, a source to point back to) and proposes nothing, which is what a
    contract about to be read at cold start needs, or reference material.
  */
  const [route, setRoute] = useState(true)
  const documents = useQuery(documentsQuery(engagementId))

  const upload = useMutation({
    mutationFn: ({ file, routed }: { file: File; routed: boolean }) => uploadDocument(engagementId, file, routed),
    onSuccess: (_result, { file, routed }) => {
      setOversize(null)
      setLastUpload(routed
        ? `${file.name} accepted. Watch its row below; proposals land on Home.`
        : `${file.name} accepted. Watch its row below.`)
      // The row should appear on the upload, not on the next tick.
      queryClient.invalidateQueries({ queryKey: ['eng', engagementId, 'documents'] })
    },
  })

  return (
    <section className={instance ? 'surface surface-wide' : 'surface'}>
      <header className="surface-head">
        <h1>Inputs</h1>
        <p className="surface-sub">
          {instance
            ? 'What J.E.D.I. has read from the customer’s ServiceNow instance.'
            : 'Transcripts, contracts, files. Upload lands the canonical bytes, and extraction '
              + 'routes what it finds: proposals to Home, work to Work.'}
        </p>
      </header>

      <div className="today-filter" role="group" aria-label="Inputs">
        <button
          type="button"
          className={`today-filter-chip${instance ? '' : ' today-filter-on'}`}
          aria-pressed={!instance}
          onClick={() => void navigate({ search: {} })}
        >
          Documents
        </button>
        <button
          type="button"
          className={`today-filter-chip${instance ? ' today-filter-on' : ''}`}
          aria-pressed={instance}
          onClick={() => void navigate({ search: { view: 'servicenow' } })}
        >
          ServiceNow instance
        </button>
      </div>

      {instance ? (
        <InstanceProfile
          engagementId={engagementId}
          connectionId={connection}
          onPick={(id) => void navigate({ search: { view: 'servicenow', connection: id } })}
        />
      ) : (
        <>
          <div className="upload">
            <input
              ref={fileInput}
              type="file"
              accept=".txt,.docx,.pdf"
              className="upload-input"
              onChange={(event) => {
                const file = event.target.files?.[0]
                event.target.value = ''
                if (!file) return
                // The ceiling is checked BEFORE the POST (wave-7 close, demo find
                // 1). An oversized body never reaches our endpoint — Kestrel
                // rejects it at the transport with a bodiless 413, so the
                // deliberate refusal ladder had a hole at its top rung and the
                // PM read "API 413 on /engagements/…". Refusing here is both the
                // honest words and the kinder act: a 55 MB file is not pushed
                // across the wire to be told no.
                if (file.size > MAX_UPLOAD_BYTES) {
                  setOversize(oversizeWords(file))
                  return
                }
                setOversize(null)
                upload.mutate({ file, routed: route })
              }}
            />
            <button
              type="button"
              className="upload-button"
              disabled={upload.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {upload.isPending ? 'Uploading…' : 'Upload a document'}
            </button>
            {/* Where a new engagement starts (Erik's demo-environment rulings,
                2026-10-22): cold start was reachable only from the moment an
                engagement was created, so a presenter had to type its address. */}
            <Link to="/cold-start" className="upload-coldstart">
              Start from the contract
            </Link>
            <label className="upload-route">
              <input type="checkbox" checked={route} onChange={(e) => setRoute(e.target.checked)} />
              Route its statements into proposals
            </label>
            <p className="upload-note">
              Accepted formats: .txt, .docx, .pdf, up to {MAX_UPLOAD_LABEL} per file.
            </p>
            {oversize && <p className="upload-error">{oversize}</p>}
            {upload.error && (
              // The server's words travel to the eyes (wave-7 step 2): a refusal
              // names its reason — the ruled size ceiling, an empty file — instead
              // of a generic guess. The one refusal with no words of its own is
              // the transport's bodiless 413, which the check above now prevents
              // and this backstops for anything that still slips past it.
              <p className="upload-error">
                {upload.error.message.includes('API 413')
                  ? `That file is over the ${MAX_UPLOAD_LABEL} limit per file. The upload was `
                    + 'refused before it finished sending. Nothing was stored.'
                  : upload.error.message}
              </p>
            )}
            {lastUpload && <p className="upload-done">{lastUpload}</p>}
          </div>

          <h2 className="surface-h2 docs-head">Documents</h2>
          {documents.isPending && <p className="surface-note">Loading…</p>}
          {documents.data?.length === 0 && (
            <p className="surface-note">Nothing here yet. Upload a document above.</p>
          )}
          <div className="docs-list">
            {documents.data?.map((document) => (
              <DocumentRowView key={document.id} engagementId={engagementId} row={document} />
            ))}
          </div>
        </>
      )}
    </section>
  )
}

// Plain words for the ingest's own states. "routed" is what the log calls
// it; "read" is what happened.
const STATE_WORDS: Record<string, string> = {
  uploaded: 'uploaded',
  extracting: 'extracting…',
  routed: 'read',
  failed: 'failed',
}

/**
 * One document's standing: where its ingest got to, and what it produced. A
 * document is somewhere, or it failed and says where and why — nothing sits
 * in an unnamed middle (the honest-doors discipline, wave-7 step 2). The
 * prop is `row`, never `document`: the global of that name is one typo away.
 */
function DocumentRowView({ engagementId, row }: { engagementId: string; row: DocumentRow }) {
  const read = row.state === 'routed'
  // Read without routing (Erik's demo-environment rulings, 2026-10-22): kept and
  // searchable, and nothing proposed from it. "0 proposals" would read as if
  // J.E.D.I. had looked for some and found none, so it says what happened.
  const notRouted = read && row.routed === false
  const zone = usePersonZone()
  const parts = [
    read ? `${row.chunkCount} passage${row.chunkCount === 1 ? '' : 's'}` : null,
    read && !notRouted ? `${row.setsRouted} proposal${row.setsRouted === 1 ? '' : 's'}` : null,
    row.uploadedByName,
    whenIn(row.uploadedAt, zone),
  ].filter(Boolean)

  return (
    <div className="docs-row-block">
      <div className="docs-row">
        <span className={`docs-state docs-state-${row.state}`}>
          {notRouted ? 'read, not routed' : STATE_WORDS[row.state] ?? row.state}
        </span>
        <button
          type="button"
          className="docs-name"
          onClick={() =>
            void apiDownload(
              `/engagements/${engagementId}/documents/${row.id}/versions/${row.documentVersion}/content`,
              row.fileName,
            )
          }
        >
          {row.fileName}
        </button>
        <span className="docs-meta">{parts.join(' · ')}</span>
      </div>
      {row.state === 'failed' && (
        <p className="docs-failure">
          Stopped at {row.failureStage}. {row.failureReason} Upload it again to retry.
        </p>
      )}
    </div>
  )
}
